import { Card } from '@app/components/blocks/card/Card'
import {
  AvatarStack,
  type ProfileAvatar,
} from '@app/components/blocks/avatarStack/AvatarStack'
import { IconButton } from '@app/components/core/button/IconButton'
import {
  DropdownMenu,
  type DropdownMenuItem,
} from '@app/components/core/dropdown/DropdownMenu'
import { Image } from '@app/components/core/image/Image'
import { Text } from '@app/components/core/typography/Text'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { expandImage, type StoredImage } from '@app/types/StoredImage'
import { useLanguage, useTranslations } from '@i18n/useLanguage'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCalendar,
  faCalendarClock,
  faClock,
} from '@fortawesome/pro-light-svg-icons'
import { faEllipsisVertical, faShare } from '@fortawesome/pro-solid-svg-icons'
import { type IconProp } from '@fortawesome/fontawesome-svg-core'
import { type Style } from 'inlines'
import { useMemo } from 'react'

export interface EventCardProps {
  image?: string | StoredImage
  imageAlt?: string
  title: string
  // Start of the event — accepts a Date, ISO string, or epoch ms.
  start: Date | string | number
  // Optional end of the event; when present a time range is rendered.
  end?: Date | string | number
  attendees?: ProfileAvatar[]
  totalAttendees?: number
  onShare?: () => void
  moreMenuItems?: DropdownMenuItem[]
  past?: boolean
  style?: Style
  'data-testid'?: string
  to?: string
}

// Mirrors the icon-next-to-text pattern used by DataCard's IconList: a leading
// FontAwesome icon followed by a Text label, vertically centred in a Row.
const MetaItem = ({ icon, text }: { icon: IconProp; text: string }) => (
  <Row gap="0.5rem" style={{ alignItems: 'center' }}>
    <FontAwesomeIcon
      icon={icon}
      style={{
        color: 'var(--palette-blue-500)',
        height: '1rem',
        width: '1rem',
        flexShrink: 0,
      }}
    />
    <Text.Small
      color="var(--palette-blue-500)"
      style={{ whiteSpace: 'nowrap' }}
    >
      {text}
    </Text.Small>
  </Row>
)

const toDate = (value: Date | string | number) =>
  value instanceof Date ? value : new Date(value)

const EventDate = ({ start, end }: { start: Date; end?: Date }) => {
  const [language] = useLanguage()

  if (start && !end) {
    return (
      <Row gap="1.5rem" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <MetaItem
          icon={faCalendar}
          text={start.toLocaleDateString(language, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        />
        <MetaItem
          icon={faClock}
          text={start.toLocaleTimeString(language, {
            hour: '2-digit',
            minute: '2-digit',
          })}
        />
      </Row>
    )
  } else if (start && end && start.toDateString() === end.toDateString()) {
    return (
      <Row gap="1.5rem" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <MetaItem
          icon={faCalendar}
          text={start.toLocaleDateString(language, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        />
        <MetaItem
          icon={faClock}
          text={`${start.toLocaleTimeString(language, {
            hour: '2-digit',
            minute: '2-digit',
          })} - ${end.toLocaleTimeString(language, {
            hour: '2-digit',
            minute: '2-digit',
          })}`}
        />
      </Row>
    )
  } else if (start && end) {
    return (
      <Row gap="1.5rem" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <MetaItem
          icon={faCalendarClock}
          text={`${start.toLocaleString(language, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })} - ${end.toLocaleString(language, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}`}
        />
      </Row>
    )
  }
}

export const EventCard = ({
  image,
  imageAlt,
  title,
  start,
  end,
  attendees,
  totalAttendees,
  onShare,
  moreMenuItems,
  past,
  style,
  'data-testid': testId = 'event-card',
  to,
}: EventCardProps) => {
  const expandedImage = expandImage(image, imageAlt ?? title)
  const t = useTranslations().app

  const isPast = useMemo(
    () =>
      past ??
      (() => {
        const now = Date.now()
        if (end) {
          return toDate(end).getTime() < now
        }

        const startOfToday = new Date(now)
        startOfToday.setHours(0, 0, 0, 0)
        return toDate(start).getTime() < startOfToday.getTime()
      })(),
    [past, end, start],
  )

  return (
    <Card
      data-testid={testId}
      style={{
        borderRadius: '1.5rem',
        border: '1px solid var(--palette-blue-50)',
        backgroundColor: isPast ? 'var(--gray-100)' : undefined,
        ...style,
      }}
      contentStyle={{
        padding: '1rem',
        flexDirection: 'row',
        gap: '1rem',
        alignItems: 'flex-start',
      }}
      to={to}
    >
      <Row
        gap="1rem"
        style={{
          flex: 1,
          minWidth: 0,
          alignItems: 'flex-start',
          ...(isPast ? { filter: 'grayscale(1)', opacity: 0.65 } : {}),
        }}
      >
        {expandedImage.src && (
          <Image
            src={expandedImage.src}
            alt={expandedImage.altText}
            resizeWidth={124}
            style={{
              width: '7.75rem',
              height: '7.75rem',
              minWidth: '7.75rem',
              flexShrink: 0,
              borderRadius: '1rem',
              objectFit: 'cover',
            }}
          />
        )}

        <Column gap="1rem" style={{ alignSelf: 'stretch', minWidth: 0 }}>
          <Title.H5
            style={{
              margin: 0,
              color: 'var(--palette-blue-800)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {title}
          </Title.H5>

          <Column gap="1rem" style={{ justifyContent: 'space-between' }}>
            <EventDate
              start={toDate(start)}
              end={end ? toDate(end) : undefined}
            />

            <div style={isPast ? { opacity: 0.5 } : undefined}>
              {attendees && attendees.length > 0 ? (
                <AvatarStack
                  size="extra-small"
                  avatars={attendees}
                  total={totalAttendees}
                />
              ) : (
                <Text.Small
                  color="var(--palette-blue-500)"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {t.eventsPage.noAttendees}
                </Text.Small>
              )}
            </div>
          </Column>
        </Column>
      </Row>

      <Row gap="0.5rem" style={{ flexShrink: 0, alignItems: 'center' }}>
        {onShare && (
          <IconButton
            variant="secondary"
            size="small"
            shape="square"
            icon={faShare}
            onClick={() => onShare?.()}
          />
        )}

        {moreMenuItems && moreMenuItems.length > 0 && (
          <DropdownMenu items={moreMenuItems}>
            <IconButton
              variant="secondary"
              size="small"
              shape="square"
              icon={faEllipsisVertical}
              onClick={() => {}}
            />
          </DropdownMenu>
        )}
      </Row>
    </Card>
  )
}
